/**
 * Amener un bâtiment à son palier MAXIMUM, en gemmes — **logique pure** (ROADMAP 2.5).
 *
 * ══ Ce que ce module corrige, et ce n'est pas une offre manquante ════════════
 *
 * Le bouton « Améliorer » du panneau de bâtiment est **désactivé et muet** dès que le
 * joueur n'a pas les pièces (`ui/buildingPanel.ts`). C'est exactement le défaut que la
 * 1.3 a corrigé dans la boutique — « plus de bouton non cliquable », demande de Benoit
 * du 19 août — et il est resté entier ici, là où le joueur passe le plus de temps une
 * fois son terrain plein.
 *
 * ══ POURQUOI CE MODULE N'INVENTE AUCUN PRIX ══════════════════════════════════
 *
 * `GEMMES.md` G§0 interdit d'ajouter un prix sans en discuter, et on n'en ajoute pas :
 * c'est celui de `core/topUp.ts`, lui-même dérivé de l'échelle `GEM_SPENDING.skip`
 * publiée dans le panneau gemmes. Améliorer contre des gemmes, c'est acheter le TEMPS
 * qu'il aurait fallu attendre pour gagner les pièces — un joueur peut déjà le faire en
 * trois gestes (avance, puis amélioration, puis amélioration). Ce module retire des taps
 * à une action déjà permise, au prix déjà publié.
 *
 * Trois conséquences, toutes héritées de `topUpCoins` et non re-écrites ici :
 *
 * 1. l'achat **consomme un des trois créneaux quotidiens** — un plafond qu'on peut
 *    contourner par un autre bouton n'est pas un plafond ;
 * 2. il **ne crédite pas `totalProduced`** : ni jalon, ni étoile de prestige, ni rang de
 *    ligue. Les gemmes achètent des pièces, jamais de la progression (§14) ;
 * 3. il est refusé quand la ville ne produit rien — sans production, « le temps qu'il
 *    aurait fallu attendre » est infini, et aucun prix juste n'existe.
 *
 * ══ Loi 1 du G§0, vérifiée ═══════════════════════════════════════════════════
 *
 * « Un joueur qui ne paie jamais obtient-il la même chose, plus lentement ? » — oui : le
 * palier 3 s'achète en pièces, et le niveau **retombe à zéro au changement d'ère**, donc
 * rien de permanent ne s'achète ici. C'est du confort récurrent, pas un plafond.
 *
 * ⚠️ Ce module NE CONTREDIT PAS la règle de `topUpCoins` (« n'achète pas l'objet visé »).
 * Cette règle protège d'un complément accroché à une carte dont le prix affiché est en
 * PIÈCES : le joueur y verrait un prix et en paierait un autre. Ici l'objet et le prix
 * sont nommés dans le même bouton — « Niveau max · 12 💎 » — et il n'y a aucune ambiguïté
 * sur ce qu'on achète ni sur ce qu'on paie.
 */

import { isLandmark } from '@/config/landmarks';
import { activeIsland, upgradeBuilding, upgradeCost } from './gameState';
import type { CitySnapshot } from './gameState';
import { quoteTopUp, topUpCoins } from './topUp';
import type { TopUpRefusal } from './topUp';

export type MaxLevelRefusal = TopUpRefusal | 'not-found' | 'maxed';

export interface MaxLevelQuote {
  /** Paliers restant à acheter. Zéro si le bâtiment est déjà au maximum. */
  readonly levels: number;
  /** Coût total en pièces des paliers restants, améliorations successives comprises. */
  readonly coins: number;
  /** Prix en gemmes du complément, `null` si aucun prix juste n'existe. */
  readonly gems: number | null;
  /** Compléments encore disponibles aujourd'hui — partagés avec l'avance de production. */
  readonly remainingToday: number;
  /** Pourquoi l'offre n'est pas proposable, `null` si elle l'est. */
  readonly refusal: MaxLevelRefusal | null;
}

/**
 * Coût total en pièces pour monter au palier maximum.
 *
 * ⚠️ Il se SIMULE palier par palier, il ne se multiplie pas. `upgradeCost` dépend du
 * niveau courant (`× building.level`) : compter « deux fois le prix du palier suivant »
 * sous-facturerait le second, et le joueur paierait un prix que le jeu ne pratique pas.
 */
function remainingCost(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): { levels: number; coins: number } {
  let working = snapshot;
  let coins = 0;
  let levels = 0;

  // Trois paliers au catalogue : la borne empêche une boucle infinie si la règle change.
  for (let step = 0; step < 3; step++) {
    const cost = upgradeCost(working, buildingId);
    if (cost <= 0) break;
    coins += cost;
    levels++;
    // On monte le palier dans une copie DOTÉE des pièces : c'est le coût qu'on mesure,
    // pas la capacité du joueur à le payer.
    const applied = upgradeBuilding({ ...working, coins: cost }, buildingId, now);
    if (!applied.ok) break;
    working = applied.snapshot;
  }

  return { levels, coins };
}

/** Ce que coûterait le passage au palier maximum — **sans rien modifier**. */
export function quoteMaxLevel(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): MaxLevelQuote {
  const building = activeIsland(snapshot).buildings.find((b) => b.id === buildingId);
  if (building === undefined) {
    return { levels: 0, coins: 0, gems: null, remainingToday: 0, refusal: 'not-found' };
  }

  /*
    Un point de repère n'a pas de paliers : le pack n'en rend qu'un modèle, et un palier 2
    le ferait disparaître de la ville faute de sprite. Le refus est le même que pour un
    bâtiment déjà au maximum — il l'est, par construction.
  */
  if (isLandmark(building.type)) {
    return { levels: 0, coins: 0, gems: null, remainingToday: 0, refusal: 'maxed' };
  }

  const { levels, coins } = remainingCost(snapshot, buildingId, now);
  const base = quoteTopUp(snapshot, coins, now);
  if (levels === 0) {
    return {
      levels: 0,
      coins: 0,
      gems: null,
      remainingToday: base.remainingToday,
      refusal: 'maxed',
    };
  }

  return {
    levels,
    coins,
    gems: base.gems,
    remainingToday: base.remainingToday,
    refusal: base.refusal,
  };
}

export interface MaxLevelResult {
  readonly snapshot: CitySnapshot;
  readonly ok: boolean;
  readonly refusal?: MaxLevelRefusal;
  /** Paliers réellement achetés. */
  readonly levels: number;
  /** Gemmes réellement dépensées. */
  readonly gems: number;
}

/**
 * Monte le bâtiment au palier maximum contre des gemmes.
 *
 * L'ordre n'est pas indifférent : on complète le solde **une seule fois**, pour le coût
 * total, puis on applique les paliers. Compléter à chaque palier consommerait autant de
 * créneaux quotidiens qu'il y a de paliers — trois taps pour un plafond de trois.
 */
export function buyMaxLevel(
  snapshot: CitySnapshot,
  buildingId: string,
  now: number,
): MaxLevelResult {
  const quote = quoteMaxLevel(snapshot, buildingId, now);
  if (quote.refusal !== null) {
    return { snapshot, ok: false, refusal: quote.refusal, levels: 0, gems: 0 };
  }

  const funded = topUpCoins(snapshot, quote.coins, now);
  if (!funded.ok) {
    return { snapshot, ok: false, refusal: funded.refusal ?? 'invalid', levels: 0, gems: 0 };
  }

  let working = funded.snapshot;
  let levels = 0;
  for (let step = 0; step < quote.levels; step++) {
    const applied = upgradeBuilding(working, buildingId, now);
    if (!applied.ok) break;
    working = applied.snapshot;
    levels++;
  }

  /*
    Si un palier échouait après le complément, le joueur aurait payé des gemmes pour des
    pièces qu'il n'a pas demandées. Le cas ne devrait pas se produire — le complément
    crédite EXACTEMENT le coût simulé — mais un « ne devrait pas » n'est pas une garantie :
    on rend l'état d'origine plutôt qu'un état à moitié acheté.
  */
  if (levels < quote.levels) {
    return { snapshot, ok: false, refusal: 'invalid', levels: 0, gems: 0 };
  }

  return { snapshot: working, ok: true, levels, gems: funded.gems };
}
